import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, Calendar, User, Phone, Mail, Paperclip } from 'lucide-react';
import { useUser } from '@/hooks/use-user';
import { OpenLeadInterestedList } from '@/pages/dashboard/components/content/OpenLeadInterestedList';

interface LeadData {
  id: string;
  leadTitle: string;
  leadDescription: string;
  leadType: string;
  leadTimeline: string;
  leadImage: string | null;
  leadAttachments: string | string[];
  personName: string | null;
  contactNumber: string | null;
  email: string | null;
  authorId: string;
  authorFirstName: string | null;
  authorLastName: string | null;
  authorImage: string | null;
  toUserId: string | null;
  toBusinessId: string | null;
  createdAt: string;
  communityId: string;
  isInterested: boolean | null;
  status: string | null;
}

export default function LeadDetailPage() {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const [lead, setLead] = useState<LeadData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchLead = async () => {
      try {
        const { data } = await axiosInstance.get(`/api/leads/${leadId}`);
        setLead(data);
      } catch (err) {
        console.error(err);
        setError('Lead not found or unavailable');
      } finally {
        setLoading(false);
      }
    };

    if (leadId) fetchLead();
  }, [leadId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex justify-center items-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <h2 className="text-xl font-semibold mb-4">{error || 'Lead not found'}</h2>
        <Button onClick={() => navigate(-1)} variant="outline">
          <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
        </Button>
      </div>
    );
  }

  const authorName =
    `${lead.authorFirstName || ''} ${lead.authorLastName || ''}`.trim() || 'Unknown';
  const attachments: string[] =
    typeof lead.leadAttachments === 'string'
      ? JSON.parse(lead.leadAttachments || '[]')
      : lead.leadAttachments || [];

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b bg-background sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button onClick={() => navigate(-1)} variant="ghost" size="icon" className="-ml-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <span className="font-semibold">Lead Details</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto py-8 px-4 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">{lead.leadTitle}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Posted by {authorName} • {new Date(lead.createdAt).toLocaleDateString()}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-xl border">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Timeline</p>
              <p className="text-sm font-medium">{lead.leadTimeline || 'Not specified'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-xl border">
            <User className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Type</p>
              <p className="text-sm font-medium">
                {lead.leadType === 'SELF' ? 'Self Requirement' : 'Referral'}
              </p>
            </div>
          </div>
          {lead.contactNumber && (
            <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-xl border">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Contact</p>
                <p className="text-sm font-medium">{lead.contactNumber}</p>
              </div>
            </div>
          )}
          {lead.email && (
            <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-xl border">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="text-sm font-medium">{lead.email}</p>
              </div>
            </div>
          )}
        </div>

        {lead.personName && lead.leadType === 'SOMEONE_ELSE' && (
          <div className="p-4 bg-muted/20 rounded-xl border">
            <p className="text-xs text-muted-foreground mb-1">Beneficiary</p>
            <p className="font-medium">{lead.personName}</p>
          </div>
        )}

        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Description
          </h3>
          <div className="p-4 bg-muted/30 rounded-xl border text-sm leading-relaxed whitespace-pre-wrap">
            {lead.leadDescription}
          </div>
        </div>

        {lead.leadImage && (
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Image
            </h3>
            <div className="rounded-xl overflow-hidden border">
              <img src={lead.leadImage} alt="Lead" className="w-full h-auto object-cover" />
            </div>
          </div>
        )}

        {attachments.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Attachments
            </h3>
            <div className="space-y-2">
              {attachments.map((att, idx) => (
                <a
                  key={idx}
                  href={att}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-muted/30 hover:bg-muted/50 border rounded-xl transition-all"
                >
                  <Paperclip className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Attachment {idx + 1}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {user?.id === lead.authorId && !lead.toUserId && !lead.toBusinessId && (
          <OpenLeadInterestedList leadId={lead.id} />
        )}
      </div>
    </div>
  );
}
